import {
  BadgeCheckIcon,
  CaretDownIcon,
  MapLocationIcon,
  RatingStars,
} from "@App/Components/common/Icons";
import { useCountry } from "@App/hooks/useCountry";
import type { State } from "@App/state";
import type { Garage } from "@App/types";
import { OrganizationIsAyvensOrDBConnect } from "@App/utils/conditionals/combinations";
import { GarageHasPhone } from "@App/utils/conditionals/garages";
import { OrganizationHasCheckedByBadge } from "@App/utils/conditionals/organization";
import GoogleMapReact from "google-map-react";
import { type FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import BackButton from "./BackButton";

type ProfileHeaderProps = {
  garage: Garage;
  displayReviews?: boolean;
};

export const ProfileHeader: FunctionComponent<ProfileHeaderProps> = ({
  garage,
  displayReviews,
}: ProfileHeaderProps) => {
  const { t } = useTranslation();
  const [mapActive, setMapActive] = useState(false);
  const organization = useSelector((state: State) => state.orders.organization);
  const countryCode = useCountry();

  return (
    <section className="container mx-auto mt-6">
      <div className="flex flex-col md:flex-row justify-between mb:md-5">
        <div>
          <h1 className="text-2xl xs:text-3xl font-serif mb-1">{garage.name}</h1>
          {OrganizationHasCheckedByBadge(organization) && (
            <span className="flex items-center gap-1 mb-3">
              <BadgeCheckIcon className={"mr-1 text-primary"} />{" "}
              {t("profileHeader.checked-by", { organization: organization.name })}
            </span>
          )}

          {displayReviews && garage.rating && (
            <a
              href="#bewertungen"
              className="flex items-center text-neutral-700 font-normal no-underline"
            >
              <RatingStars className={"mr-1"} type={"rating"} percent={garage.rating} />
              <span className="font-bold mr-1">
                {((garage.rating / 100) * 5).toFixed(2).replace(".", ",") ?? "0"}
              </span>{" "}
              ({garage.review_count || "0"})
              <CaretDownIcon className={"rotate-[-90deg] h-2 ml-1"} />
            </a>
          )}

          {displayReviews && !garage.rating && <small>{t("profileHeader.no-reviews")}</small>}

          <div className="mt-5 flex items-start justify-between">
            {garage.nationwide_service && (
              <div>
                {t("profileHeader.nationwide-service-1", { context: countryCode })}
                <br />
                {t("profileHeader.nationwide-service-2")}
              </div>
            )}
            <div>
              {garage.street}
              <br />
              {garage.postcode} {garage.city}
              <br />
              {OrganizationIsAyvensOrDBConnect(organization) &&
                GarageHasPhone(garage) &&
                garage.phone}
            </div>

            <button
              className="border border-primary rounded shadow md:hidden p-2 ml-2"
              onClick={() => setMapActive(!mapActive)}
            >
              <img src="/images/icons/icon-map.svg" alt="" />
            </button>
          </div>
          <BackButton className="mt-2" />
        </div>
        <div
          className={`md:ml-auto mt-3 mt-md-0 position-relative md:block w-full md:w-96 h-64 md:h-36 ${mapActive ? " block" : "hidden"}`}
        >
          <div style={{ height: "100%", width: "100%" }}>
            <GoogleMapReact
              bootstrapURLKeys={{ key: "AIzaSyCyOdtBEoG0atpfWsNR1VRMZPEgib8MVzY" }}
              defaultCenter={{ lat: garage.lat, lng: garage.lon }}
              defaultZoom={11}
            >
              <MapLocationIcon key={garage.id} lat={garage.lat} lng={garage.lon} />
            </GoogleMapReact>
          </div>
        </div>
      </div>
    </section>
  );
};
